import React, { ChangeEvent } from 'react';
import { Flex, Text, FormLabel, Box, BoxProps, Input } from '@chakra-ui/react';
import { faFilePdf, faFileImage, faFileVideo } from '@fortawesome/pro-solid-svg-icons';
import { Icon } from '~/ui-components';
import { useLocalization } from '~/framework';

type FileInputProps = Omit<BoxProps, 'onChange'> & {
   onChange: (e: ChangeEvent<HTMLInputElement>) => any;
   onDropFn: (file: File) => any;
};

export function FileInput({ onChange, onDropFn, ...boxProps }: FileInputProps) {
   const { _js } = useLocalization();
   const [dragActive, setDragActive] = React.useState(false);
   const [error, setError] = React.useState('');

   const onHandleDrag = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.type === 'dragenter' || e.type === 'dragover') {
         setDragActive(true);
      } else if (e.type === 'dragleave') {
         setDragActive(false);
      }
   };

   const onHandleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files) {
         if (e.dataTransfer.files.length > 1) {
            setError('Only one file can be uploaded at a time.');
            return;
         }

         onDropFn(e.dataTransfer.files[0]);
      }
   };

   return (
      <Box {...boxProps}>
         <Box border="2px dashed" borderRadius="md" borderWidth="1px" borderColor="gray.400" p="4">
            <FormLabel htmlFor="file-input" margin="0" onDragEnter={onHandleDrag}>
               <Flex
                  direction="column"
                  alignItems="center"
                  bg="white"
                  p="6"
                  borderRadius="md"
                  cursor="pointer"
                  transition="background-color 0.3s ease"
                  _hover={{ backgroundColor: 'blue.100' }}
                  backgroundColor={dragActive ? 'blue.100' : 'white'}
               >
                  <Flex gap={1} mb="6">
                     <Icon
                        type={faFilePdf}
                        fontSize="45px"
                        color="orange.500"
                        sx={{ transform: 'rotate(-15deg)' }}
                        mt="2"
                     />
                     <Icon type={faFileImage} fontSize="54px" color="blue.500" />
                     <Icon
                        type={faFileVideo}
                        fontSize="45px"
                        color="green.500"
                        sx={{ transform: 'rotate(15deg)' }}
                        mt="2"
                     />
                  </Flex>
                  <Text fontSize="lg">{_js('Drag & drop here')}</Text>
                  <Text fontSize="lg" color="blue.500">
                     {_js('or browse for files')}
                  </Text>
               </Flex>
               {dragActive && (
                  <Box
                     position="absolute"
                     right="0"
                     top="0"
                     bottom="0"
                     left="0"
                     onDragEnter={onHandleDrag}
                     onDragLeave={onHandleDrag}
                     onDragOver={onHandleDrag}
                     onDrop={onHandleDrop}
                  />
               )}
            </FormLabel>
            <Input id="file-input" type="file" multiple={false} hidden onChange={onChange} />
         </Box>
         {error && (
            <Text mt="4" fontSize="lg" textColor="red.500">
               {error}
            </Text>
         )}
      </Box>
   );
}
